// src/components/Shots/AddShotModal.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Alert, Card } from "react-bootstrap";
import { useGetSquadDataQuery } from "../../slices/squadDataApiSlice";
import PitchSvg from "../PitchSvg";
import {
  calculateShotExpectedScoreNew,
  calculateVerticalWind,
} from "../../utils/expectedScore";
import { getShotMapCalibration } from "../../utils/shotMapCalibration";
import { FaQuestionCircle } from "react-icons/fa";

const DEFAULT_SET_PLAYS = [
  "Open Play",
  "Free Kick from Hands",
  "Free Kick from Ground",
  "Penalty",
  "45m Kick",
  "Mark",
];
const DEFAULT_SHOT_TYPES = ["Goal attempt", "2 Point attempt", "Point attempt"];
const DEFAULT_METHODS = ["Left foot", "Right foot", "Hand"];
const DEFAULT_PRESSURES = ["Low", "Medium", "High"];
const DEFAULT_OUTCOMES = [
  "Goal",
  "2 Point",
  "Point",
  "Wide",
  "Post",
  "Short",
  "Blocked",
  "Saved",
  "45",
];

// PIXEL CALIBRATION
const {
  INNER_LEFT_PX,
  INNER_TOP_PX,
  INNER_RIGHT_PX,
  INNER_BOTTOM_PX,
  X_PCT_OFFSET,
  X_PCT_SCALE,
  Y_PCT_OFFSET,
  Y_PCT_SCALE,
} = getShotMapCalibration();

const INNER_WIDTH_PX = INNER_RIGHT_PX - INNER_LEFT_PX;
const INNER_HEIGHT_PX = INNER_BOTTOM_PX - INNER_TOP_PX;
const formatShotTypeLabel = (value) =>
  String(value || "").replace(/\s*attempt$/i, "");

export default function AddShotModal({
  show,
  onHide,
  onAdd,
  game = {},
  previousSelectedHalf,
  setPreviousSelectedHalf,
  previousShotTime = "00:00:00",
  setPlays = DEFAULT_SET_PLAYS,
  shotTypes = DEFAULT_SHOT_TYPES,
  methods = DEFAULT_METHODS,
  pressures = DEFAULT_PRESSURES,
  outcomes = DEFAULT_OUTCOMES,
}) {
  const homeTeam = game?.HomeTeam ?? "";
  const awayTeam = game?.AwayTeam ?? "";

  //Add correct for wind
  const windStrength = Number(game?.WindStrength) || 0;
  const windAngle = Number(game?.WindAngle) || 0;

  const { data: squadData } = useGetSquadDataQuery();
  const firstHalfVideoStartSeconds = Number(game?.FirstHalfVideoTime);
  const secondHalfVideoStartSeconds = Number(game?.SecondHalfVideoTime);
  const hasHalfVideoTimes =
    Number.isFinite(firstHalfVideoStartSeconds) &&
    Number.isFinite(secondHalfVideoStartSeconds);


  /* NOT USED
  const toHhMmSs = (totalSeconds) => {
    const safe = Number.isFinite(totalSeconds) ? totalSeconds : 0;
    const minutes = Math.floor(safe / 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor(safe % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  */

  const initialForm = useMemo(
    () => ({
      gameHalf: previousSelectedHalf === "2" || previousSelectedHalf === "3" ? previousSelectedHalf : "1",
      gameTime: previousShotTime,
      teamShot: homeTeam,
      playerName: "",
      setPlay: "Open Play",
      shotType: "Point attempt",
      shotMethod: "Right foot",
      shotPressure: "Low",
      shotOutcome: "Point",
      newAttack: "Yes",
      distance: "",
      angle: "",
      side: "",
    }),
    [homeTeam, previousSelectedHalf, previousShotTime],
  );
  const [form, setForm] = useState(initialForm);
  const [dotPos, setDotPos] = useState({ xSvg: null, ySvg: null });
  const [errors, setErrors] = useState({});
  const mapRef = useRef(null);
  const basicsRef = useRef(null);
  const timingRef = useRef(null);
  const playerPlayRef = useRef(null);
  const methodOutcomeRef = useRef(null);
  const pressureAttackRef = useRef(null);
  const mapSectionRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);

  // Reset form when opening
  useEffect(() => {
    if (show) {
      setForm({ ...initialForm, teamShot: homeTeam });
      setDotPos({ xSvg: null, ySvg: null });
      setErrors({});
      setShowTutorial(false);
      setTutorialStepIndex(0);
      setTutorialRect(null);
    }
  }, [show, homeTeam, initialForm]);

  const tutorialSteps = useMemo(
    () => [
      {
        title: "Basics",
        body: "Start by setting the team and match half. In Gaelic football, the half matters because the video timestamp is converted against the game's stored half-start time.",
        ref: basicsRef,
      },
      {
        title: "Video Time and Shot Type",
        body: "Enter the time where the shot appears in the video, not the in-game clock. The app calculates in-game time from this using the game's 1st-half or 2nd-half video anchor.",
        ref: timingRef,
      },
      {
        title: "Player and Set Play",
        body: "Choose the player who took the shot and the set-play context, such as open play, free kick, penalty, mark, or 45. This is important for later analysis.",
        ref: playerPlayRef,
      },
      {
        title: "Method and Pressure",
        body: "Method records how the shot was taken, such as left foot, right foot, or hand. Pressure records how contested the attempt was: Low means time and space, Medium means some defensive pressure, and High means a rushed or heavily disrupted shot.",
        ref: methodOutcomeRef,
      },
      {
        title: "Outcome and New Attack",
        body: "Outcome records the result of the attempt, whether it was a score, wide, blocked, saved, or another result. In Gaelic football, New Attack should be on when this shot starts a new possession phase, and off when it belongs to the same attacking sequence as the previous shot.",
        ref: pressureAttackRef,
      },
      {
        title: "Place the Shot",
        body: "Click the pitch to place the shot location. This sets distance, angle, and side automatically, which then feeds the expected-score model.",
        ref: mapSectionRef,
      },
    ],
    [],
  );

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null);
      return;
    }
    const step = tutorialSteps[tutorialStepIndex];
    const node = step?.ref?.current;
    if (!node) {
      setTutorialRect(null);
      return;
    }
    const rect = node.getBoundingClientRect();
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    });
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) return undefined;
    const step = tutorialSteps[tutorialStepIndex];
    step?.ref?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    const refresh = () => updateTutorialRect();
    refresh();
    const timeoutId = window.setTimeout(refresh, 220);
    const secondTimeoutId = window.setTimeout(refresh, 420);
    const intervalId = window.setInterval(refresh, 70);
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 800);
    window.addEventListener("resize", refresh);
    window.addEventListener("scroll", refresh, true);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(secondTimeoutId);
      window.clearTimeout(stopIntervalId);
      window.clearInterval(intervalId);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("scroll", refresh, true);
    };
  }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialRect(null);
  };

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStepIndex((prev) => prev + 1);
  };

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const getTutorialCardStyle = () => {
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 240 + gap < viewportHeight;
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 240 - gap);
    const preferredLeft = tutorialRect.left;
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
    const left = Math.min(Math.max(16, preferredLeft), maxLeft);
    return {
      position: "fixed",
      top,
      left,
      width: cardWidth,
      zIndex: 1062,
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
      transition: "top 160ms ease, left 160ms ease",
    };
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    //convert to string if checkbox toggled
    if (name === "newAttack"){
      value = e.target.checked ? "Yes" : "No"
    }

    setForm((f) => ({
      ...f,
      [name]: value,
      ...(name === "teamShot" ? { playerName: "" } : {}),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const onMapClick = (e) => {
  const rect = mapRef.current.getBoundingClientRect();
  const pctX = (e.clientX - rect.left) / rect.width;
  const pctY = (e.clientY - rect.top) / rect.height;

    const pitchX = pctX;
    const pitchY = pctY;
    const dx = (pitchX - X_PCT_OFFSET) / X_PCT_SCALE;
    const dy = (pitchY - Y_PCT_OFFSET) / Y_PCT_SCALE;

    const dist = Math.hypot(dx, dy);
    const ang = (Math.atan2(Math.abs(dx), dy) * 180) / Math.PI;
    const side = dx < 0 ? "Left" : "Right";

    setForm((f) => ({
      ...f,
      distance: dist.toFixed(2),
      angle: ang.toFixed(0),
      side,
    }));

    const xPitchPct = X_PCT_OFFSET + X_PCT_SCALE * dx;
    const yPitchPct = Y_PCT_OFFSET + Y_PCT_SCALE * dy;
    const xSvg = INNER_LEFT_PX + xPitchPct * INNER_WIDTH_PX;
    const ySvg = INNER_TOP_PX + yPitchPct * INNER_HEIGHT_PX;
    setDotPos({ xSvg, ySvg });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.map;
      return next;
    });
  };

  const getTimeInSeconds = value => {
    if (!/^\d{1,2}:[0-5]\d:[0-5]\d$/.test(value || "")) return NaN;
    const [hh, mm, ss] = value.split(":").map(Number)
    if (ss > 59 || mm > 59) {
      return NaN
    } 

    return ((hh * 60 * 60) + (mm * 60) + ss)
  }

  const isValidHHMMSS = (value) => /^\d{1,2}:[0-5]\d:[0-5]\d$/.test(String(value || "").trim());

  const getHalfMismatchWarning = (gameHalf, shotTimeSeconds) => {
    if (!hasHalfVideoTimes || !Number.isFinite(shotTimeSeconds)) return "";
    if (
      gameHalf === "1" &&
      (shotTimeSeconds < firstHalfVideoStartSeconds ||
        shotTimeSeconds >= secondHalfVideoStartSeconds)
    ) {
      return "Are you sure? Time does not match 1st half.";
    }
    if (
      (gameHalf === "2" || gameHalf === "3") &&
      shotTimeSeconds < secondHalfVideoStartSeconds
    ) {
      return `Are you sure? Time is before ${gameHalf === "2" ? "2nd" : "3rd"} half start.`;
    }
    return "";
  };

  const getDerivedGameTimeSeconds = (gameHalf, enteredTimeSeconds) => {
    if (!Number.isFinite(enteredTimeSeconds)) return NaN;
    if (String(gameHalf) === "1") {
      if (!Number.isFinite(firstHalfVideoStartSeconds)) return NaN;
      return Math.max(0, enteredTimeSeconds - firstHalfVideoStartSeconds);
    }
    if (String(gameHalf) === "2") {
      if (!Number.isFinite(secondHalfVideoStartSeconds)) return NaN;
      return Math.max(0, enteredTimeSeconds - secondHalfVideoStartSeconds);
    }
    if (String(gameHalf) === "3") {
      if (!Number.isFinite(secondHalfVideoStartSeconds)) return NaN;
      return Math.max(0, enteredTimeSeconds - secondHalfVideoStartSeconds);
    }
    return enteredTimeSeconds;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!teamHasPlayers) {
      nextErrors.playerName = `No players found for ${form.teamShot}. Add players for this team before adding a shot.`;
    }

    // -- VALIDATION --
    // Required fields
    const required = {
      "Game Half": form.gameHalf,
      "Video Time": form.gameTime,
      Team: form.teamShot,
      Player: form.playerName,
      "Set Play": form.setPlay,
      "Shot Type": form.shotType,
      Method: form.shotMethod,
      Pressure: form.shotPressure,
      Outcome: form.shotOutcome,
      "New Attack?": form.newAttack,
    };
    for (const [label, val] of Object.entries(required)) {
      if (!val) {
        const keyMap = {
          "Game Half": "gameHalf",
          "Video Time": "gameTime",
          Team: "teamShot",
          Player: "playerName",
          "Set Play": "setPlay",
          "Shot Type": "shotType",
          Method: "shotMethod",
          Pressure: "shotPressure",
          Outcome: "shotOutcome",
          "New Attack?": "newAttack",
        };
        nextErrors[keyMap[label]] = `${label} is required.`;
      }
    }

    if (form.gameTime && !isValidHHMMSS(form.gameTime)) {
      nextErrors.gameTime =
        "Invalid input: Video Time must match requested format HH:MM:SS (e.g. 00:07:35).";
    }

    const enteredTimeSeconds = isValidHHMMSS(form.gameTime)
      ? getTimeInSeconds(form.gameTime)
      : NaN;
    if (form.gameHalf === "1" && !Number.isFinite(firstHalfVideoStartSeconds)) {
      nextErrors.gameTime =
        "Set the game's First Half Video Time before adding a 1st-half shot.";
    }
    if (
      (form.gameHalf === "2" || form.gameHalf === "3") &&
      !Number.isFinite(secondHalfVideoStartSeconds)
    ) {
      nextErrors.gameTime =
        `Set the game's Second Half Video Time before adding a ${form.gameHalf === "2" ? "2nd" : "3rd"}-half shot.`;
    }
    const derivedGameTimeSeconds = getDerivedGameTimeSeconds(
      form.gameHalf,
      enteredTimeSeconds,
    );
    if (form.gameTime && Number.isNaN(derivedGameTimeSeconds)) {
      nextErrors.gameTime =
        nextErrors.gameTime ||
        "Unable to calculate game time from the entered video time.";
    }
      /* 
    if (hasHalfVideoTimes) {
      if (
        form.gameHalf === "1" &&
        (shotTimeSeconds < firstHalfVideoStartSeconds ||
          shotTimeSeconds >= secondHalfVideoStartSeconds)
      ) {
        nextErrors.gameHalf =
          `Invalid input: 1st half shot time must be between ${secondsToHHMMSS(firstHalfVideoStartSeconds)} and ${secondsToHHMMSS(secondHalfVideoStartSeconds)}.`;
      }
      if (
        (form.gameHalf === "2" || form.gameHalf === "3") &&
        shotTimeSeconds < secondHalfVideoStartSeconds
      ) {
        nextErrors.gameHalf =
          `Invalid input: ${form.gameHalf === "2" ? "2nd" : "3rd"} half shot time must be at or after ${secondsToHHMMSS(secondHalfVideoStartSeconds)}.`;
      }
    }
      */

    // Map click required
    if (dotPos.xSvg == null || dotPos.ySvg == null) {
      nextErrors.map = "Invalid input: Please place the shot on the pitch.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    // - end validation -

    const distNum = Number(form.distance);
    const angNum = Number(form.angle);
    // const vertWind = calculateVerticalWind(
    //   game?.WindStrength,
    //   game?.WindAngle,
    //   angNum,
    // );

    // Determine if direction of play with +1 if teamShot is homeTeam, -1 if awayTeam for first half (or third half)  and opposite for second half. This assumes data entry from the perspective of the home team attacking left-to-right, which is how the video footage is usually recorded.
    const dirShot = (form.teamShot === homeTeam ? 1 : -1) * (form.gameHalf.toString() === "2" ? -1 : 1);
    const vertWind = dirShot * windStrength * Math.cos((windAngle * Math.PI) / 180);

    //console.log("Calculated vertical wind:", vertWind);

    const expectedScore = calculateShotExpectedScoreNew(
      distNum,
      angNum,
      form.shotPressure,
      form.setPlay,
      form.shotMethod,
      form.side,
      form.shotType,
      vertWind,
      game?.GameType || "Mens",
    );

    const payload = {
      gameHalf: form.gameHalf,
      gameTimeSeconds: derivedGameTimeSeconds,
      teamShot: form.teamShot,
      playerName: form.playerName,
      setPlay: form.setPlay,
      shotType: form.shotType,
      shotMethod: form.shotMethod,
      shotPressure: form.shotPressure,
      shotOutcome: form.shotOutcome,
      newAttack: form.newAttack,
      distance: distNum,
      angle: angNum,
      side: form.side,
      expectedScore,
      gameId: game?._id,
      gameDate: new Date(game.GameDate).getTime(),
      homeTeam,
      awayTeam,
    };

    try {
      await onAdd(payload);
      if (typeof setPreviousSelectedHalf === "function") {
        setPreviousSelectedHalf(form.gameHalf);
      }
      // Keep half + time, reset all other fields/default selections.
      setForm({
        ...initialForm,
        gameHalf: form.gameHalf,
        gameTime: form.gameTime,
        teamShot: homeTeam,
      });
      setDotPos({ xSvg: null, ySvg: null });
      setErrors({});
    } catch (err) {
      // Parent handles toast/error feedback.
    }
  };

  const playersForTeam = useMemo(
    () =>
      (squadData?.entries || [])
        .filter((p) => p.team === form.teamShot)
        .map((p) => p.playerName),
    [squadData, form.teamShot],
  );
  const teamHasPlayers = playersForTeam.length > 0;
  const shotTimeSecondsForWarning = isValidHHMMSS(form.gameTime)
    ? getTimeInSeconds(form.gameTime)
    : NaN;
  const halfMismatchWarning = getHalfMismatchWarning(
    form.gameHalf,
    shotTimeSecondsForWarning,
  );

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        dialogClassName="shot-modal-wide"
        centered
        contentClassName="bg-dark text-white shot-modal-content"
      >
        <button
          type="button"
          className="btn-close btn-close-white shot-modal-close"
          aria-label="Close"
          onClick={onHide}
        />
        <Modal.Body as={Row} className="text-white shot-modal-body shot-modal-body--no-header">
          <Col md={5} className="shot-modal-form-col">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
              <h2 className="shot-modal-side-title mb-0">Add Shot</h2>
              <Button
                variant="outline-light"
                size="sm"
                type="button"
                onClick={startTutorial}
                className="d-inline-flex align-items-center gap-2 flex-shrink-0"
              >
                <FaQuestionCircle />
                Help
              </Button>
            </div>
            <Form className="shot-modal-form" noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Row className="g-2 align-items-start shot-modal-form-row" ref={basicsRef}>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Team</Form.Label>
                  <div>
                    {[homeTeam, awayTeam]
                      .filter(Boolean)
                      .map((team) => (
                      <Form.Check
                        key={team}
                        inline
                        type="radio"
                        id={`add-shot-team-${team}`}
                        name="teamShot"
                        label={team}
                        value={team}
                        checked={form.teamShot === team}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                  {errors.teamShot && (
                    <Form.Text className="text-danger d-block">{errors.teamShot}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Game Half</Form.Label>
                  <div className="shot-modal-radio-row shot-modal-radio-row--compact">
                    {["1", "2", "3"].map((half) => (
                      <Form.Check
                        key={half}
                        inline
                        type="radio"
                        id={`add-shot-half-${half}`}
                        name="gameHalf"
                        label={half}
                        value={half}
                        checked={form.gameHalf === half}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                  {errors.gameHalf && (
                    <Form.Text className="text-danger d-block">{errors.gameHalf}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 align-items-start shot-modal-form-row" ref={timingRef}>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Video Time (HH:MM:SS)</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    placeholder="00:00:00"
                    name="gameTime"
                    value={form.gameTime}
                    onChange={handleChange}
                    pattern="^\\d{1,2}:[0-5]\\d:[0-5]\\d$"
                    required
                  />
                  {errors.gameTime && (
                    <Form.Text className="text-danger d-block">{errors.gameTime}</Form.Text>
                  )}
                  {!errors.gameTime && halfMismatchWarning && (
                    <Form.Text className="text-warning d-block small">{halfMismatchWarning}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={7} className="ps-md-2">
                <Form.Group className="mb-3">
                  <Form.Label>Shot Type</Form.Label>
                  <div>
                    {shotTypes.map((st, idx) => (
                      <Form.Check
                        key={st}
                        inline
                        type="radio"
                        id={`add-shot-type-${idx}`}
                        name="shotType"
                        label={formatShotTypeLabel(st)}
                        value={st}
                        checked={form.shotType === st}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                  {errors.shotType && (
                    <Form.Text className="text-danger d-block">{errors.shotType}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 shot-modal-form-row" ref={playerPlayRef}>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Player</Form.Label>
                  {!teamHasPlayers && form.teamShot && (
                    <Alert variant="warning" className="py-2 mb-2">
                      You cannot add a shot for "{form.teamShot}" because this team has no players.
                    </Alert>
                  )}
                  <Form.Select
                    name="playerName"
                    value={form.playerName}
                    onChange={handleChange}
                    disabled={!teamHasPlayers}
                    required
                  >
                    <option value="">Select player</option>
                    {playersForTeam.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.playerName && (
                    <Form.Text className="text-danger d-block">{errors.playerName}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Set Play</Form.Label>
                  <Form.Select
                    name="setPlay"
                    value={form.setPlay}
                    onChange={handleChange}
                    required
                  >
                    {setPlays.map((sp) => (
                      <option key={sp} value={sp}>
                        {sp}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.setPlay && (
                    <Form.Text className="text-danger d-block">{errors.setPlay}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 shot-modal-form-row" ref={methodOutcomeRef}>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Method</Form.Label>
                    <div>
                      {methods.map((m, idx) => (
                        <Form.Check
                          key={m}
                          type="radio"
                          id={`add-shot-method-${idx}`}
                          name="shotMethod"
                          label={m}
                          value={m}
                          checked={form.shotMethod === m}
                          onChange={handleChange}
                          required
                        />
                      ))}
                    </div>
                    {errors.shotMethod && (
                      <Form.Text className="text-danger d-block">{errors.shotMethod}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Pressure</Form.Label>
                    <div>
                      {pressures.map((p, idx) => (
                        <Form.Check
                          key={p}
                          type="radio"
                          id={`add-shot-pressure-${idx}`}
                          name="shotPressure"
                          label={p}
                          value={p}
                          checked={form.shotPressure === p}
                          onChange={handleChange}
                          required
                        />
                      ))}
                    </div>
                    {errors.shotPressure && (
                      <Form.Text className="text-danger d-block">{errors.shotPressure}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 align-items-end shot-modal-form-row" ref={pressureAttackRef}>
                <Col md={9}>
                  <Form.Group className="mb-3">
                    <Form.Label>Outcome</Form.Label>
                    <Form.Select
                      name="shotOutcome"
                      value={form.shotOutcome}
                      onChange={handleChange}
                      required
                    >
                      {outcomes.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.shotOutcome && (
                      <Form.Text className="text-danger d-block">{errors.shotOutcome}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="d-flex flex-column align-items-start mb-3">
                    <Form.Label>New Attack?</Form.Label>
                    <Form.Check
                      type="switch"
                      aria-label="New Attack?"
                      name="newAttack"
                      checked={form.newAttack === "Yes"}
                      value={form.newAttack === "Yes"}
                      onChange={handleChange}
                      className="mt-1"
                      id="newAttackCheckBox"
                    />
                    {errors.newAttack && (
                      <Form.Text className="text-danger d-block">{errors.newAttack}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            <Button
              type="submit"
              variant="warning"
              className="w-100 text-dark fw-semibold"
              disabled={!teamHasPlayers}
            >
              Add Shot
            </Button>
          </Form>
          </Col>
          <Col md={7} ref={mapSectionRef}>
            <div
              className="shot-modal-map"
              ref={mapRef}
              onClick={onMapClick}
              style={{
                position: "relative",
                width: "100%",
                paddingTop: `${(INNER_HEIGHT_PX / INNER_WIDTH_PX) * 100}%`,
              }}
            >
              <PitchSvg
                className="pitch-bg"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "crosshair",
                }}
              />
              <svg
                className="pitch-overlay"
                viewBox={`${INNER_LEFT_PX} ${INNER_TOP_PX} ${INNER_WIDTH_PX} ${INNER_HEIGHT_PX}`}
                preserveAspectRatio="xMidYMid meet"
              >
                {dotPos.xSvg != null && (
                  <circle
                    cx={dotPos.xSvg}
                    cy={dotPos.ySvg}
                    r="4"
                    fill="#ff1900"
                    stroke="#ff1900"
                  />
                )}
              </svg>
            </div>
            {errors.map && (
              <Form.Text className="text-danger d-block mt-2">{errors.map}</Form.Text>
            )}
          </Col>
        </Modal.Body>
      </Modal>
      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: tutorialRect.top,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: "100vw",
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left,
                  width: tutorialRect.width,
                  height: tutorialRect.height,
                  border: "2px solid #f59e0b",
                  borderRadius: 12,
                  pointerEvents: "none",
                  zIndex: 1061,
                  transition:
                    "top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease",
                }}
              />
              <Card className="border-warning" style={getTutorialCardStyle()}>
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>
                    {tutorialStepIndex + 1} / {tutorialSteps.length}{" "}
                    {tutorialSteps[tutorialStepIndex].title}
                  </strong>
                  <Button
                    variant="link"
                    className="text-dark p-0 text-decoration-none fw-semibold"
                    onClick={closeTutorial}
                  >
                    Close
                  </Button>
                </Card.Header>
                <Card.Body className="bg-dark text-white">
                  <p className="mb-3">{tutorialSteps[tutorialStepIndex].body}</p>
                  <div className="d-flex justify-content-between gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={tutorialStepIndex === 0}
                      onClick={prevTutorialStep}
                    >
                      Back
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="text-dark fw-semibold"
                      onClick={nextTutorialStep}
                    >
                      {tutorialStepIndex === tutorialSteps.length - 1 ? "Finish" : "Next"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(8, 12, 18, 0.72)",
                zIndex: 1060,
              }}
            />
          )}
        </>
      )}
    </>
  );
}

